<template>
  <div class="sse-monitor" v-if="showMonitor && hasMonitorAccess">
    <div class="monitor-header">
      <h4>🔗 SSE 연결 모니터</h4>
      <button @click="showMonitor = false" class="close-btn">×</button>
    </div>
    
    <div class="monitor-content">
      <!-- 연결 상태 -->
      <div class="status-section">
        <div class="status-item" :class="connectionStatusClass">
          <span class="status-dot"></span>
          <span>{{ connectionStatusText }}</span>
        </div>
        
        <div class="status-item" :class="healthStatusClass">
          <span class="status-dot"></span>
          <span>{{ healthStatusText }}</span>
        </div>
      </div>
      
      <!-- 연결 정보 -->
      <div class="info-section">
        <div class="info-item">
          <label>연결 시간:</label>
          <span>{{ formatTime(connectionInfo?.lastConnected) }}</span>
        </div>
        
        <div class="info-item">
          <label>마지막 Heartbeat:</label>
          <span>{{ formatTime(lastHeartbeat) }}</span>
        </div>
        
        <div class="info-item" v-if="lastHeartbeat">
          <label>Heartbeat 경과:</label>
          <span>{{ heartbeatElapsed }}초</span>
        </div>
        
        <div class="info-item">
          <label>재연결 시도:</label>
          <span>{{ reconnectAttempts }}/{{ maxReconnectAttempts }}</span>
        </div>
      </div>
      
      <!-- 액션 버튼 -->
      <div class="action-section">
        <button @click="connect" :disabled="isConnecting" class="action-btn connect">
          {{ isConnecting ? '연결 중...' : '연결' }}
        </button>
        
        <button @click="disconnect" :disabled="!isConnected" class="action-btn disconnect">
          연결 해제
        </button>
        
        <button @click="reconnect" :disabled="isConnecting" class="action-btn reconnect">
          재연결
        </button>
      </div>
      
      <!-- 알람 테스트 섹션 -->
      <div class="test-section">
        <h5>🧪 알람 테스트</h5>
        <div class="test-actions">
          <button @click="testBrowserNotification" class="test-btn">
            🔔 브라우저 알림
          </button>
          
          <button @click="testToastMessage" class="test-btn">
            💬 토스트 메시지
          </button>
          
          <button @click="testSSENotification" class="test-btn" :disabled="!isConnected">
            📡 SSE 알림 시뮬레이션
          </button>
          
          <button @click="testSystemMessage" class="test-btn">
            🔧 시스템 메시지 테스트
          </button>
          
          <button @click="testDataUpdate" class="test-btn">
            📊 데이터 업데이트 테스트
          </button>
          
          <button @click="testEnhancedNotification" class="test-btn">
            🔔 향상된 알림 테스트
          </button>
          
          <button @click="testGlobalEvent" class="test-btn">
            🧪 직접 이벤트 테스트
          </button>
          
          <button @click="requestNotificationPermission" class="test-btn">
            🔓 알림 권한 요청
          </button>
          
          <button @click="testHeartbeat" class="test-btn" :disabled="!isConnected">
            💗 Heartbeat 시뮬레이션
          </button>
          
          <button @click="clearAllLogs" class="test-btn">
            🗑️ 로그 초기화
          </button>
        </div>
        
        <div class="test-info">
          <small>
            알림 권한: <span :class="notificationPermissionClass">{{ notificationPermissionText }}</span>
          </small>
        </div>
      </div>
      
      <!-- 통계 -->
      <div class="stats-section" v-if="isConnected">
        <div class="stat-item">
          <label>알림:</label>
          <span>{{ notifications.length }}개 ({{ unreadNotificationCount }}개 미읽음)</span>
        </div>
        
        <div class="stat-item">
          <label>메시지:</label>
          <span>{{ messages.length }}개</span>
        </div>
      </div>
      
      <!-- 최근 로그 -->
      <div class="log-section">
        <h5>최근 로그</h5>
        <div class="log-list">
          <div v-for="log in recentLogs" :key="log.id" class="log-item" :class="log.type">
            <span class="log-time">{{ formatLogTime(log.timestamp) }}</span>
            <span class="log-message">{{ log.message }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- 모니터 토글 버튼 (그룹 권한 2번만 접근 가능) -->
  <div class="monitor-toggle" v-if="!showMonitor && hasMonitorAccess">
    <button @click="showMonitor = true" class="toggle-btn">
      🔗 SSE 모니터
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useSSE } from '@/js/composables/useSSE';
import { useStore } from 'vuex';
import GmxGlobal from '@/js/global';
import RequsetApI from '@/js/request/requsetApI';
//import AuthUtil from '@/js/store/modules/unit/AuthUtil';
const showMonitor = ref(false);
const store = useStore();

const {
  isConnected,
  isConnecting,
  isHealthy,
  connectionInfo,
  lastHeartbeat,
  reconnectAttempts,
  maxReconnectAttempts,
  notifications,
  messages,
  unreadNotificationCount,
  connect,
  disconnect,
  reconnect,
  simulateNotification
  // notificationSettings,      // 향후 사용 예정
  // updateNotificationSettings  // 향후 사용 예정
} = useSSE({ autoConnect: false });

// 그룹 권한 체크 (그룹 권한 2번만 모니터 접근 가능)
const hasMonitorAccess = computed(() => {
  try {
    const userInfo = store.getters['systemInfo/getUserInfo'] || {};
    const grpSeq = userInfo.authGrpSeq;
    return grpSeq === 2 || grpSeq === '2';
  } catch (error) {
    console.warn('권한 체크 실패:', error);
    return false;
  }
});

// 연결 상태 클래스
const connectionStatusClass = computed(() => ({
  'status-connected': isConnected.value,
  'status-connecting': isConnecting.value,
  'status-disconnected': !isConnected.value && !isConnecting.value
}));

const connectionStatusText = computed(() => {
  if (isConnected.value) return '연결됨';
  if (isConnecting.value) return '연결 중...';
  return '연결 끊김';
});

const healthStatusClass = computed(() => ({
  'status-healthy': isHealthy.value,
  'status-unhealthy': !isHealthy.value && isConnected.value
}));

const healthStatusText = computed(() => {
  if (!isConnected.value) return '상태 알 수 없음';
  return isHealthy.value ? '정상' : '비정상';
});

// Heartbeat 경과 시간
const heartbeatElapsed = ref(0);
let heartbeatTimer = null;

const updateHeartbeatElapsed = () => {
  if (lastHeartbeat.value) {
    const now = new Date();
    const elapsed = Math.floor((now - new Date(lastHeartbeat.value)) / 1000);
    heartbeatElapsed.value = elapsed;
  }
};

// 로그 시스템
const logs = ref([]);
const maxLogs = 20;

const addLog = (message, type = 'info') => {
  logs.value.unshift({
    id: Date.now() + Math.random(),
    timestamp: new Date(),
    message,
    type
  });
  
  if (logs.value.length > maxLogs) {
    logs.value = logs.value.slice(0, maxLogs);
  }
};

const recentLogs = computed(() => logs.value.slice(0, 10));

// 알림 권한 상태
const notificationPermission = ref(
  typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
);

const notificationPermissionClass = computed(() => ({
  'permission-granted': notificationPermission.value === 'granted',
  'permission-denied': notificationPermission.value === 'denied',
  'permission-default': notificationPermission.value === 'default',
  'permission-unsupported': notificationPermission.value === 'unsupported'
}));

const notificationPermissionText = computed(() => {
  switch (notificationPermission.value) {
    case 'granted': return '허용됨';
    case 'denied': return '차단됨';
    case 'default': return '미설정';
    default: return '지원안함';
  }
});

// 알람 테스트 함수들
const testBrowserNotification = () => {
  if (typeof Notification === 'undefined') {
    addLog('브라우저 알림이 지원되지 않습니다', 'error');
    return;
  }
  
  if (Notification.permission === 'granted') {
    new Notification('🧪 테스트 알림', {
      body: '브라우저 알림이 정상적으로 작동합니다!',
      icon: '/favicon.ico',
      tag: 'test-notification'
    });
    addLog('브라우저 알림 테스트 실행됨', 'success');
  } else {
    addLog('브라우저 알림 권한이 필요합니다', 'warning');
  }
};

const testToastMessage = () => {

  store.dispatch(GmxGlobal.STORE_COMMON_TOAST, {
    type: GmxGlobal.MSG_STATE_OK,
    title: '테스트 토스트',
    msg: '토스트 메시지가 정상적으로 작동합니다!'
  });
  addLog('토스트 메시지 테스트 실행됨', 'success');
};

const testSSENotification = () => {
  if (!isConnected.value) {
    addLog('❌ SSE 연결이 필요합니다', 'error');
    addLog('💡 먼저 "연결" 버튼을 클릭하여 SSE를 연결해주세요', 'info');
    return;
  }
  
  addLog('✅ SSE 연결 상태 확인됨', 'success');
  addLog('📡 DB에 테스트 알림 생성 중...', 'info');
  addLog('🔍 서버에서 SSE를 통해 알림을 보내는지 모니터링 시작', 'info');
  
  // 실제 DB에 테스트 알림 데이터 저장
  const testAlarmData = {
    alarmCd: 'ALM003',
    eventUrl : 'test',
    eventCont : `SSE 테스트 알림 - ${new Date().toLocaleTimeString()}`,
    refTable: 'ct_orders',
    refKey: 'GM-25-179',
    targetUserId:'ahj5229'
  };
  
  RequsetApI.insertMtAlarmEvent(testAlarmData, (response) => {
    if (response.data && response.data.status === 'ok') {
      addLog('✅ DB에 테스트 알림 저장 완료', 'success');
      
      const eventSeq = response.data.result?.eventSeq || Date.now();
      addLog(`📊 저장된 알림 ID: ${eventSeq}`, 'info');
      
      // 직접 처리하지 않고 서버에서 SSE를 통해 보내주길 기다림
      addLog('⏳ 서버에서 SSE를 통해 알림을 보내주길 기다리는 중...', 'info');
      addLog('📡 SSE 연결이 정상이라면 곧 알림이 수신될 것입니다', 'info');
      
      // 30초 후 수신되지 않았으면 경고 메시지
      setTimeout(() => {
        addLog('⚠️ 30초 동안 SSE 알림이 수신되지 않았습니다. 서버 SSE 설정을 확인해주세요.', 'warning');
      }, 30000);
      
    } else {
      addLog('❌ DB 저장 실패: ' + (response.data?.message || '알 수 없는 오류'), 'error');
      addLog('💡 DB 저장이 실패하면 서버에서 SSE 알림도 보내지 않습니다', 'warning');
    }
  });
};

const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') {
    addLog('브라우저 알림이 지원되지 않습니다', 'error');
    return;
  }
  
  try {
    const permission = await Notification.requestPermission();
    notificationPermission.value = permission;
    
    if (permission === 'granted') {
      addLog('알림 권한이 허용되었습니다', 'success');
      // 권한 허용 후 테스트 알림 표시
      setTimeout(() => {
        new Notification('✅ 권한 허용됨', {
          body: '이제 브라우저 알림을 받을 수 있습니다!',
          icon: '/favicon.ico'
        });
      }, 500);
    } else {
      addLog(`알림 권한: ${permission}`, 'warning');
    }
  } catch (error) {
    addLog('알림 권한 요청 실패: ' + error.message, 'error');
  }
};

const testHeartbeat = () => {
  if (!isConnected.value) {
    addLog('SSE 연결이 필요합니다', 'warning');
    return;
  }
  
  // 가짜 heartbeat 시뮬레이션
  store.commit('sse/SET_LAST_HEARTBEAT', new Date());
  addLog('Heartbeat 시뮬레이션 실행됨', 'success');
};

const clearAllLogs = () => {
  logs.value = [];
  addLog('로그가 초기화되었습니다', 'info');
};

// 새로운 시뮬레이션 함수들
const testSystemMessage = () => {
  simulateNotification('system');
  addLog('🔧 시스템 메시지 테스트 실행됨', 'success');
};

const testDataUpdate = () => {
  simulateNotification('update');
  addLog('📊 데이터 업데이트 테스트 실행됨', 'success');
};

const testEnhancedNotification = () => {
  simulateNotification('notification');
  addLog('🔔 향상된 알림 테스트 실행됨', 'success');
};

// 직접 전역 이벤트 테스트
const testGlobalEvent = () => {
  const testNotification = {
    eventSeq: Date.now(),
    id: Date.now(),
    alarmNm: '직접 이벤트 테스트',
    alarmDesc: '전역 이벤트 시스템 테스트입니다.',
    eventCont: '이 알림은 직접 전역 이벤트로 발생시킨 것입니다.',
    userNm: '테스트 사용자',
    alarmType: 'info',
    creDat: new Date().toISOString(),
    readYn: 'N',
    read: false
  };
  
  console.log('🧪 Manually dispatching new-notification-received event:', testNotification);
  window.dispatchEvent(new CustomEvent('new-notification-received', {
    detail: testNotification
  }));
  
  addLog('🧪 직접 전역 이벤트 테스트 실행됨', 'success');
};

// 시간 포맷팅
const formatTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleTimeString();
};

const formatLogTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('ko-KR', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// SSE 수신 감지를 위한 전역 이벤트 리스너
const handleSSETestResult = (event) => {
  const notification = event.detail;
  addLog(`🎉 SSE 알림 수신 성공! ID: ${notification.eventSeq || notification.id}`, 'success');
  addLog(`📝 알림 내용: ${notification.eventCont || notification.message}`, 'info');
  addLog('✅ 서버 → 클라이언트 SSE 통신이 정상 작동합니다', 'success');
};

// 라이프사이클
onMounted(() => {
  // Heartbeat 타이머 시작 (1초마다 → 5초마다로 변경하여 리소스 절약)
  heartbeatTimer = setInterval(updateHeartbeatElapsed, 5000);
  
  // SSE 테스트 결과 감지를 위한 이벤트 리스너
  window.addEventListener('new-notification-received', handleSSETestResult);
  
  // 초기 로그
  addLog('SSE 모니터 시작됨 (최적화된 리소스 사용)', 'info');
  
  // 알림 권한 상태 주기적 체크 (10초마다)
  const permissionCheckTimer = setInterval(() => {
    if (typeof Notification !== 'undefined') {
      notificationPermission.value = Notification.permission;
    }
  }, 10000);
  
  // 상태 변화 감지를 위한 watch 설정
  watch(isConnected, (connected) => {
    if (connected) {
      addLog('SSE 연결됨', 'success');
    } else {
      addLog('SSE 연결 끊김', 'warning');
    }
  });
  
  watch(isHealthy, (healthy) => {
    if (isConnected.value) {
      if (healthy) {
        addLog('연결 상태 정상', 'success');
      } else {
        addLog('연결 상태 비정상 감지', 'error');
      }
    }
  });
  
  // cleanup 함수에 타이머 정리 추가
  onUnmounted(() => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
    }
    if (permissionCheckTimer) {
      clearInterval(permissionCheckTimer);
    }
    
    // 이벤트 리스너 정리
    window.removeEventListener('new-notification-received', handleSSETestResult);
  });
});

// 모니터는 기본적으로 닫힌 상태로 시작 (수동으로만 열기)
</script>

<style scoped>
.sse-monitor {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 350px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 9999;
  font-size: 12px;
}

.monitor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
  border-radius: 8px 8px 0 0;
}

.monitor-header h4 {
  margin: 0;
  font-size: 14px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.monitor-content {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.status-section {
  margin-bottom: 16px;
}

.status-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-connected .status-dot { background: #28a745; }
.status-connecting .status-dot { background: #ffc107; }
.status-disconnected .status-dot { background: #dc3545; }
.status-healthy .status-dot { background: #28a745; }
.status-unhealthy .status-dot { background: #fd7e14; }

.info-section {
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 11px;
}

.info-item label {
  font-weight: 500;
  color: #666;
}

.action-section {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.action-btn {
  flex: 1;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: background-color 0.2s;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.connect { background: #28a745; color: white; }
.connect:hover:not(:disabled) { background: #218838; }

.disconnect { background: #dc3545; color: white; }
.disconnect:hover:not(:disabled) { background: #c82333; }

.reconnect { background: #007bff; color: white; }
.reconnect:hover:not(:disabled) { background: #0056b3; }

.test-section {
  margin-bottom: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.test-section h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #333;
}

.test-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.test-btn {
  flex: 0 1 calc(50% - 4px);
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: background-color 0.2s;
  background: #6c757d;
  color: white;
}

.test-btn:hover:not(:disabled) {
  background: #545b62;
}

.test-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.test-info {
  margin-top: 8px;
  text-align: right;
}

.test-info small {
  font-size: 10px;
  color: #666;
}

.permission-granted {
  color: #28a745;
  font-weight: bold;
}

.permission-denied {
  color: #dc3545;
  font-weight: bold;
}

.permission-default {
  color: #ffc107;
  font-weight: bold;
}

.permission-unsupported {
  color: #6c757d;
  font-weight: bold;
}

.stats-section {
  margin-bottom: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 11px;
}

.stat-item label {
  font-weight: 500;
  color: #666;
}

.log-section {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.log-section h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #333;
}

.log-list {
  max-height: 120px;
  overflow-y: auto;
}

.log-item {
  display: flex;
  margin-bottom: 4px;
  font-size: 10px;
}

.log-time {
  color: #666;
  margin-right: 8px;
  min-width: 60px;
}

.log-message {
  flex: 1;
}

.log-item.success .log-message { color: #28a745; }
.log-item.warning .log-message { color: #ffc107; }
.log-item.error .log-message { color: #dc3545; }
.log-item.info .log-message { color: #333; }

.monitor-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
}

.toggle-btn {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.toggle-btn:hover {
  background: #0056b3;
}
</style> 